import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'

import { auth0UserAtom, surveyGroupFilterAtom } from '~/atoms'
import {
  type SurveyGroupQuestionFilter,
  type SurveyGroupSegmentFilter,
} from '~/gql/graphql'
import { useCurrentSurveyGroup } from '~/hooks/useCurrentSurveyGroup'
import { exists, safeGetLocalStorageItem, toSurveyGroupFilterV2 } from '~/utils'

/**
 * 古い形式の surveyGroupFilter を localStorage から取得して、新しい形式に変換して surveyGroupFilterAtom にセットする関数を返すカスタムフックです。
 *
 * @returns surveyGroupFilterAtom にセットする関数。
 */
export const useSetSurveyGroupFilterFromLocalStorageV1 = () => {
  const setSurveyGroupFilter = useSetAtom(surveyGroupFilterAtom)
  const surveyGroup = useCurrentSurveyGroup()
  const user = useAtomValue(auth0UserAtom)

  const popSurveyGroupFilterV1Item = React.useCallback(
    <T>(
      suffix: 'questionFilters' | 'segmentFilters' | 'surveyIdCountMap',
    ): T | undefined => {
      const key = [
        'analyzer',
        'surveyGroup',
        surveyGroup?.id,
        user?.sub,
        suffix,
      ].join('::')
      const value = safeGetLocalStorageItem<T>(key)

      localStorage.removeItem(key)

      return value
    },
    [surveyGroup?.id, user?.sub],
  )

  return React.useCallback(
    (questions: Parameters<typeof toSurveyGroupFilterV2>[1]) => {
      const countMap =
        popSurveyGroupFilterV1Item<Record<string, number>>('surveyIdCountMap')
      const questionFilters =
        popSurveyGroupFilterV1Item<SurveyGroupQuestionFilter[]>(
          'questionFilters',
        )
      const segmentFilters =
        popSurveyGroupFilterV1Item<SurveyGroupSegmentFilter[]>('segmentFilters')

      if (
        !exists(countMap) ||
        !exists(questionFilters) ||
        !exists(segmentFilters)
      ) {
        return
      }

      const surveyGroupFilter = {
        countMap,
        filterGroups: toSurveyGroupFilterV2(
          { questionFilters, segmentFilters },
          questions,
        ),
      }
      setSurveyGroupFilter(surveyGroupFilter)
    },
    [popSurveyGroupFilterV1Item, setSurveyGroupFilter],
  )
}
